import React from 'react';
import { ResourceType } from '../types';
import { FormattedMessage } from 'react-intl';
import { ErrorMessage } from 'modus/components/error-message/ErrorMessage';
import { useAuthorization } from './AuthorizationProvider';

interface AuthorizerProps {
  action: string;
  resourceType: ResourceType;
  resourceId: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  hideErrorMessage?: boolean;
}

export const ResourceAuthorizer: React.FC<AuthorizerProps> = ({
  action,
  resourceType,
  resourceId,
  children,
  hideErrorMessage = false,
  fallback
}) => {
  const { isActionAllowed } = useAuthorization(resourceType, resourceId);
  const isAuthorized = isActionAllowed(action);

  console.log('Authorization check:', { isAuthorized, action, resourceType, resourceId });

  if (isAuthorized) {
    return <>{children}</>;
  }

  if (hideErrorMessage) {
    return <></>;
  }
  
  if (fallback) {
    return <>{fallback}</>;
  }
  

  return (
    <ErrorMessage message={
      <FormattedMessage 
        defaultMessage="You are not authorized to {action} this {resourceType}"
        values={{
          action,
          resourceType: resourceType.toLowerCase()
        }}
      />
    }/>
  );
};

// Higher-order component version
export const withAuthorization = (
  WrappedComponent: React.ComponentType<any>,
  authProps: Omit<AuthorizerProps, 'children' | 'fallback'>
) => {
  return (props: any) => (
    <ResourceAuthorizer {...authProps}>
      <WrappedComponent {...props} />
    </ResourceAuthorizer>
  );
};

// Example usage:
// <Authorizer
//   namespace="api-section"
//   resourceType="API"
//   resourceId="123"
//   action="update"
//   fallback={<CustomUnauthorizedMessage />}
// >
//   <EditButton />
// </Authorizer>
//
// // Or with HOC:
// const ProtectedEditButton = withAuthorization(EditButton, {
//   namespace: "api-section",
//   resourceType: "API",
//   resourceId: "123",
//   action: "update"
// });
